import {
    IColorPickerManager, IColorPickerTdwReport,
} from '@src/slide/application/service/color-picker-manager/color-picker-manager.interface';
import { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import { MenuType } from '@src/slide/domain/ui-model/constant/menu';
import ReactDom from 'react-dom';
import React from 'react';
import ColorPickerContainer from '@src/js/common/ark-extensions/dc-components/color-picker/color-picker-container';
import {
    IColorPickerExtension,
    IShowOptions,
} from '@src/js/common/ark-extensions/dc-components/color-picker/type';
import ua from '@tencent/docs-user-agent';
import {
    TDWActionType, TDWModuleType,
    TDWOperationType, EventCodeType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import ArkLoader from '@src/js/common/ark-extensions';
import componentConfig from '@src/js/common/ark-extensions/dc-components/color-picker/component-config';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';

interface IColorPickerManagerProps {
    menuManager: IMenuManager;
}

const COLOR_PANEL_HEIGHT = 335;

// 色盘主题
const COLOR_PICKER_THEME_PRESET_1 = 1; // 预设主题

/**
 * 色盘管理
 * 1、管理色盘的业务回调
 * 2、管理色盘的api唤起
 * 3、色盘的组件化调用请使用<ColorPickerContainer />
 */
export default class ColorPickerManager implements IColorPickerManager {
    public useNewEyeDropper = true;
    public colorPickerService?: IColorPickerExtension;
    public themePreset = COLOR_PICKER_THEME_PRESET_1;
    private menuManager: IMenuManager;

    /**
     * api唤起的组件展示态
     * @private
     */
    private isShowColorPicker = false;
    private panelContainer?: HTMLDivElement;

    public constructor(props: IColorPickerManagerProps) {
        const { menuManager } = props;
        this.menuManager = menuManager;
        this.useNewEyeDropper = this.browserCheck() && location.protocol !== 'http:';
    }

    /**
     * 给dc色盘色值最近使用色
     */
    public setRecentColor = (color: string) => {
        this.colorPickerService?.setRecentColors?.({
            color,
        });
    };

    public setColorPickerExtension(colorPickerExtension: IColorPickerExtension) {
        this.colorPickerService = colorPickerExtension;
    }

    public handleFontColorChange = (props: { id: string, color: string, option: any }) => {
        const { id, color, option } = props;
        this.menuManager.toolbarMenu().callback(MenuType.TOOLBAR, id, color, option);
    };

    /**
     * 关闭api全局色盘
     */
    public closeGlobalColorPanel = () => {
        this.panelContainer && document.body.removeChild(this.panelContainer);
        this.isShowColorPicker = false;
        document.removeEventListener('mouseup', this.closeGlobalColorPanel);
    };

    /**
     * 上报
     * @param props
     */
    public colorPickerTdwReport(props: IColorPickerTdwReport): void {
        const { color, colorPickerFrom, colorGroupFrom, actionType } = props;
        const ver5 = actionType;
        const ver6 = colorPickerFrom;
        const ver7 = colorGroupFrom;
        const ver9 = color;

        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.COLOR_MODULE,
            action: TDWActionType.COLOR_PICKER_ACTION,
            eventcode: EventCodeType.COLOR_PICKER_EVENT_CODE,
            ver5,
            ver6,
            ver7,
            ver8: this.themePreset,
            ver9,
        });
    }

    /**
     * 如果坐标下方放不下色盘，就翻转色盘展示
     * @param top
     * @private
     */
    private getContainerTopByIsRevert(top) {
        let resTop = top;
        if (top > COLOR_PANEL_HEIGHT && document.body.offsetHeight - top < COLOR_PANEL_HEIGHT) {
            resTop = top - COLOR_PANEL_HEIGHT;
        }
        return resTop;
    }

    private handleGlobalPanelColorChange(callback, color, source) {
        callback?.(color, source);
        this.closeGlobalColorPanel();
    }

    /**
     * 检查浏览器是否支持dc取色器
     */
    /* eslint-disable complexity */
    private browserCheck = () => {
        const { isChrome, isElectronTencentDocsClient, getElectronTencentDocsClientVersion, getChromeVersion } = ua;
        if (isChrome) {
            const version = getChromeVersion();
            if (version) {
                const versionNumber = Number(version.split('.')[0]);
                // 95以上的chrome支持原生取色器
                return versionNumber >= 95;
            }
        } else if (isElectronTencentDocsClient) {
            // 客户端能保证在3.1.9以上版本支持原生取色器，再以前的版本太老了，不好捞
            const clientVersion = getElectronTencentDocsClientVersion();
            const flag = true;
            try {
                const versionList = clientVersion?.split('.').map(item => Number(item)) || [];
                // 最小的大版本号3，x
                if (versionList[0] || versionList[0] === 0) {
                    const firstNumber = versionList[0];
                    if (firstNumber < 3) {
                        return false;
                    }

                    if (firstNumber >= 4) {
                        return true;
                    }
                }

                // 最小的中版本号1, 3.x
                if (versionList[1] || versionList[1] === 0) {
                    const secondNumber = versionList[1];
                    if (secondNumber < 1) {
                        return false;
                    }

                    if (secondNumber >= 2) {
                        return true;
                    }
                }

                // 最小的小版本号9 3.1.x
                if (versionList[2] || versionList[2] === 0) {
                    const thirdNumber = versionList[2];
                    return thirdNumber >= 9;
                }
            } catch (e) {
                window.log.error('[version parse error]', e);
                return false;
            }

            return flag;
        }
        return false;
    };
}
